import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import Text from '../../../global/text/text';
import './event-links-button-wrapper.scss';
import { IEventLinkDTO } from '../../../../api/event-links.dao';
import { Popover } from '../../../global/popover/popover';
import { EventLinks } from '../../../_events/_manage-event/shared/event-links/event-links.container';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { EventLinkItem } from '../../../_events/_manage-event/shared/event-links/event-link-item/event-link-item';
import { getFormattedUrl } from '../../../../infrastructure/utils/common.utils';
import DefaultButton from '../../../global/default-button/default-button';
import { EventTrackerAddLinks } from './event-tracker-add-links.container';

export interface IEventLinksButtonWrapperProps {
    subdomain: string | null;
    accountId: number;
    eventId: number;
    links: IEventLinkDTO[];
    fetchEventsList: () => Promise<void>;
}

const EventLinksButtonWrapper = (props: IEventLinksButtonWrapperProps) => {
    const {
        subdomain,
        links = [],
        accountId,
        eventId,
        fetchEventsList,
    } = props;

    const [isViewsOpen, setIsViewsOpen] = useState<boolean>(false);
    const viewsRef = useRef();
    const isInternalEventLinksPermission = hasPermission(Permission.VIEW_INTERNAL_EVENT_LINKS);

    const LinkPopover = ({ children }: { children: JSX.Element }): JSX.Element => {
        return (
            <Popover
                isOpen={isViewsOpen}
                buttonRef={viewsRef}
                onClickOutside={e => {
                    const target = (e.target as Element)?.closest('.event-link-form-in-popover');
                    /* e.composedPath - don`t close popup by scrolling */
                    if (!target && e.composedPath().length > 3) {
                        setIsViewsOpen(false);
                    }
                }}
                customWidth={240}
                padding={0}
                align='start'
                position='bottom'
            >
                {children}
            </Popover>
        );
    };

    const getCounterElement = (linksLength: number): JSX.Element =>
        <span className='delivery-team-column__attendees-count'> {`+${linksLength}`} </span>;

    const getLinksCounter = (): JSX.Element => {
        const listLength = !!links && links.length;
        if (subdomain && listLength) {
            return getCounterElement(links.length);
        } else if ( !subdomain && listLength > 1) {
            return getCounterElement(listLength - 1);
        } else <></>;
    };

    const getLinkListPopoverWithPermission = (): JSX.Element => {
        return (
            <div
                className='event-links-button-wrapper'
                ref={viewsRef}
                onClick={() => setIsViewsOpen(true)}
            >
                <div className={classnames('delivery-team-column', isViewsOpen && 'delivery-team-column--active',)}>
                    <div className='delivery-team-column--wrapper'>
                        <Text
                            message={subdomain ? 'Passport' : (links[0].label || links[0].type)}
                            withEllipsis
                            withoutTitle
                        />
                        {getLinksCounter()}
                    </div>
                </div>
                <LinkPopover>
                    <div onClick={e => e.stopPropagation()}>
                        <EventLinks
                            links={links}
                            eventId={eventId}
                            accountId={accountId}
                            subdomain={subdomain}
                            isEventTrackerForm={true}
                            fetchEventsList={fetchEventsList}
                        />
                    </div>
                </LinkPopover>
            </div>

        );
    };

    const getLinkPopoverWithoutPermission = (): JSX.Element => {
        const formattedSubdomain = getFormattedUrl(subdomain || '');
        const passportSubdomain = {
            linkId: null,
            type: '',
            label: 'Passport',
            url: formattedSubdomain
        };
        return (
            <>
                {subdomain
                    ? <div
                        className='event-links-button-wrapper'
                        ref={viewsRef}
                        onClick={() => setIsViewsOpen(true)}
                    >
                        <div className={classnames('delivery-team-column ', isViewsOpen && 'active')}>
                            <div className='delivery-team-column--wrapper'>
                                <Text
                                    message={'Passport'}
                                    withEllipsis
                                    withoutTitle
                                />
                            </div>
                        </div>
                        <LinkPopover>
                            <div onClick={e => e.stopPropagation()}>
                                <div className='event_form__list-view'>
                                    <EventLinkItem
                                        key={null}
                                        isEventTrackerForm={true}
                                        {...passportSubdomain}
                                    />
                                </div>
                            </div>
                        </LinkPopover>
                    </div>
                    : <></>
                }
            </>
        );
    };

    const getAddLinksPopoverWithPermission = (): JSX.Element => {
        return (
            <div
                className='event-links-button-wrapper'
                ref={viewsRef}
            >
                 <DefaultButton
                    text={<FormattedMessage id='oe.common.addEventLink' description='button label' defaultMessage='+ Add Link' />}
                    className={classnames('link-item add-attendee-btn visible-on-hover', isViewsOpen && 'active',)}
                    modifier={['fat', 'confirm-safe']}
                    onClick={() => setIsViewsOpen(true)}
                />

                  <LinkPopover>
                       <EventTrackerAddLinks
                                links={links}
                                eventId={eventId}
                                accountId={accountId}
                                isEventTrackerForm={true}
                                fetchEventsList={fetchEventsList}
                                closeAddLinksPopup={()=>setIsViewsOpen(false)}
                        />

                    </LinkPopover>
            </div>
        );
    };

    return (
        <>
            {isInternalEventLinksPermission
                /* if passport subdomain and additional links dont present, show Add button and add link popup */
                ? (!subdomain && !links.length
                    ? getAddLinksPopoverWithPermission()
                    /* if links present, show link list popup */
                    : getLinkListPopoverWithPermission()
                ) : getLinkPopoverWithoutPermission()
            }
        </>
    );
};


export { EventLinksButtonWrapper };
